import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {getProcedureTypeForStudyType} from 'studies/TypedStudyUtils';
import {StudyOverviewViewQuery} from 'studies/workspace/overview/__generated/StudyOverviewViewQuery';
import StudyAnalysisListView from 'studies/workspace/overview/StudyAnalysisListView';
import Pane, {PaneBorder} from 'ui-library/pane/Pane';

import ProcedureRunDetailsView from '../design-tabs/ProcedureRunDetailsView';
import ProcedureRunPlanDetailsView from '../design-tabs/ProcedureRunPlanDetailsView';
import MultiProcedureRunAndPlanListingTable from './MultiProcedureRunAndPlanListingTable';
import {ProcedureRunTab, StudyOverviewContext} from './MultiProcedureRunAndPlanOverviewTabContents';

type StudyNode = ExtractByTypename<StudyOverviewViewQuery['study'], 'StudyNode'>;

export default function ProcedureRunAndPlanOverview({study}: {study: StudyNode}): JSX.Element {
  const {selectedProcedureRun, selectedProcedureRunPlan, currentProcedureRunTab, setCurrentProcedureRunTab} =
    StudyOverviewContext.useContext();
  return (
    <div
      className={css(
        currentProcedureRunTab === ProcedureRunTab.OVERVIEW ||
          (selectedProcedureRun.id == null && selectedProcedureRunPlan.id == null)
          ? styles.studyBackgroundGray
          : styles.studyBackgroundWhite
      )}
    >
      {selectedProcedureRun.id == null && selectedProcedureRunPlan.id == null ? (
        <div className={css(styles.runsContainer)}>
          <MultiProcedureRunAndPlanListingTable
            studyId={study.id}
            setCurrentProcedureRunTab={setCurrentProcedureRunTab}
          />
          <div className={css(styles.analysisPaneWrapper)}>
            <Pane
              title="Analyses"
              paneBorder={PaneBorder.SOLID}
              dataTestAttrs={{component: 'ProcedureRunOverview', element: 'analysesPane'}}
            >
              <div className={css(styles.analysisContainer)}>
                <StudyAnalysisListView studyId={study.id} showDate={false} />
              </div>
            </Pane>
          </div>
        </div>
      ) : selectedProcedureRunPlan.id != null ? (
        // Key on the plan id so switching between plans remounts the detail subtree instead of
        // reconciling it. Without this, transient stale plan data (served by the overview query's
        // renderCachedDataWhenRefetching) can leave design-tab state (e.g. selectedMethodId) tied to
        // the previously viewed plan, referencing methods that don't exist in the new plan.
        <ProcedureRunPlanDetailsView
          key={selectedProcedureRunPlan.id}
          studyType={study.studySchema.studyType}
          studyId={study.id}
          procedureType={getProcedureTypeForStudyType(study.studySchema.studyType)}
          selectedProcedureRunPlan={selectedProcedureRunPlan}
        />
      ) : (
        <ProcedureRunDetailsView
          key={selectedProcedureRun.id}
          studyType={study.studySchema.studyType}
          studyId={study.id}
          selectedProcedureRun={selectedProcedureRun}
          currentProcedureRunTab={currentProcedureRunTab}
          setCurrentProcedureRunTab={setCurrentProcedureRunTab}
        />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  studyBackgroundGray: {
    backgroundColor: Color.GRAY_100,
    height: '100%',
    maxHeight: '100%',
  },
  studyBackgroundWhite: {
    backgroundColor: Color.WHITE,
    height: '100%',
    maxHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  runsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    padding: '8px',
    height: '100%',
    maxHeight: '100%',
  },
  analysisPaneWrapper: {
    flex: '0 0 auto',
  },
  analysisContainer: {
    padding: '12px',
  },
});
